import { COMMA, ENTER } from '@angular/cdk/keycodes';
import {
  Component,
  Inject,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
} from '@angular/core';
import {
  UntypedFormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormArray,
  Validators,
} from '@angular/forms';
import { MatLegacyAutocompleteSelectedEvent as MatAutocompleteSelectedEvent } from '@angular/material/legacy-autocomplete';
import { MatLegacyChipInputEvent as MatChipInputEvent } from '@angular/material/legacy-chips';
import {
  MatLegacyDialogRef as MatDialogRef,
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
} from '@angular/material/legacy-dialog';
import { DateTime } from 'luxon';
import { Observable, Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

// INTERFACES
import { SettingsService } from '@app/core/settings/services/settings.service';
import { FeatureFlagValue } from '@app/feature-toggle-list';
import { FeatureToggleService } from '@app/shared/services/feature-toggle.service';
import { ICompany } from 'v2/modules/client/interfaces/client-company.interface';
import { ClientCompanyApiService } from 'v2/modules/shared/services/api/client';

import { OptionInterface } from '../../../shared/interfaces/common.interface';
import { IChannel } from '../../../shared/interfaces/mtchannel.interface';

// CONFIG
import { trackById, trackByLabel } from '../../../shared/util';
import { InvoiceConfig } from '../../config';

import {
  EAdvanceSettingsOption,
  EInvoiceCreatedFrom,
  EInvoiceSettingsDefault,
  EWarehouseDefaultGroup,
  IInvoiceType,
} from './../../interfaces';
import { ECustomFieldObject } from '../../../custom-field';

@Component({
  selector: 'lum-invoice-advance-settings-form',
  templateUrl: './invoice-advance-settings-form.component.html',
  styleUrls: ['./invoice-advance-settings-form.component.scss'],
})
export class InvoiceAdvanceSettingsFormComponent {
  @ViewChild('alternateIdInput')
  alternateIdInput :ElementRef<HTMLInputElement>;

  form :UntypedFormGroup;
  tags = new UntypedFormControl([]);
  settings = EAdvanceSettingsOption;
  frequency :string;
  client_purchase_order_type_id :number;
  client_purchase_order_type_label :string;
  payment_term_title :string;
  delivery_type :string;
  frequencyList :OptionInterface[] = InvoiceConfig.frequencyList;
  deliveryTypeList :OptionInterface[] = InvoiceConfig.deliveryTypeList;
  tagList :OptionInterface[] = [];

  separatorKeysCodes :number[] = [ENTER, COMMA];
  alternateIdsCtrl = new UntypedFormControl();
  eInvoiceCreatedFrom = EInvoiceCreatedFrom;
  currentCompany :ICompany;

  trackById = trackById;
  trackByLabel = trackByLabel;

  currentCompany$ :Observable<ICompany | null>;
  private onDestroy$ = new Subject();
  paymentTerms$ :Observable<any>;
  eCustomFieldObject = ECustomFieldObject;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data :any,
    private cd :ChangeDetectorRef,
    private clientCompanyApiService :ClientCompanyApiService,
    private dialogRef :MatDialogRef<InvoiceAdvanceSettingsFormComponent>,
    private fb :UntypedFormBuilder,
    private ffService :FeatureToggleService,
    private settingsService :SettingsService,
  ) {}

  ngOnInit() :void {
    this.initFromService();
    this.initForm();

    this.getCurrentCompany().subscribe();
    this.initObservables();
  }

  onAddAlternateId(event :MatChipInputEvent) :void {
    const value = (event.value || '').trim();

    // Add our alternateId
    if (value) {
      this.alternateIdsFormArray.push(this.fb.control(value));
    }

    // Clear the input value
    event.chipInput!.clear();

    this.alternateIdsCtrl.setValue(null);
  }

  onRemoveAlternateId(alternateId :string) :void {
    const index = this.alternateIdsFormArray.value.indexOf(alternateId);
    if (index > -1) {
      this.alternateIdsFormArray.removeAt(index);
    }
  }

  onSelectAlternateId(event :MatAutocompleteSelectedEvent) :void {
    this.alternateIdsFormArray.push(this.fb.control(event.option.viewValue));
  }

  onSetTags(data :any) :void {
    this.form.patchValue({ tags: data?.tag });
  }

  onSetInvoiceType(invoiceType :IInvoiceType) :void {
    if (invoiceType) {
      const orderDateValue = this.data.orderDate ?? DateTime.now().toISODate();
      const orderDate = DateTime.fromISO(orderDateValue);

      const dueDateDays =
        invoiceType?.dueDateDays || this.currentCompany?.due_date_days || 30;
      const dueDate = orderDate.plus({ days: dueDateDays }).toISODate();

      this.form.patchValue({
        client_purchase_order_type_id: invoiceType?.id,
        client_purchase_order_type_label: invoiceType?.label,
        due_date: dueDate,
        autopay: invoiceType.autopay,
        autopay_after_days: invoiceType.autopayAfterDays,
        autoPushUpdatesToSalesOrder: invoiceType.autoPushUpdatesToSalesOrder,
      });
    }
  }

  onSubmit() :void {
    this.dialogRef.close(this.form.value);
  }
  onClose() :void {
    this.dialogRef.close();
  }

  onCustomFieldChange(values :string) :void {
    this.form.get('customFieldValues')?.patchValue(values);
  }

  onSelectPosSalesChannel(channel :IChannel) :void {
    this.salesChannelIdControl.setValue(channel.id);
    this.warehouseGroupIdControl.setValue(channel.warehouseGroupId);
  }

  private initForm() :void {
    const {
      frequency,
      payment_term_title,
      delivery_type,
      tags,
      client_purchase_order_type_id,
      client_purchase_order_type_label,
      alternate_ids,
      customFieldValues,
      sales_channel_id,
      order_date,
      due_date,
      autopay,
      autopay_after_days,
      autoPushUpdatesToSalesOrder,
    } = this.data;

    this.form = this.fb.group({
      frequency: [frequency || EInvoiceSettingsDefault.FREQUENCY],
      payment_term_title: [
        payment_term_title || EInvoiceSettingsDefault.PAYMENT_TERM,
      ],
      delivery_type: [delivery_type || EInvoiceSettingsDefault.DELIVERY_TYPE],
      tags: [tags || []],
      client_purchase_order_type_id: [
        client_purchase_order_type_id || null,
        Validators.required,
      ],
      client_purchase_order_type_label: [client_purchase_order_type_label || ''],
      alternate_ids: alternate_ids?.length
        ? this.fb.array(
            alternate_ids.map((alternateId :string) =>
              this.fb.control(alternateId),
            ),
          )
        : this.fb.array([]),
      customFieldValues: [customFieldValues || '[]'],
      sales_channel_id: [sales_channel_id || null],
      warehouse_group_id: [this.data?.warehouse_group_id],
      defaultPosChannel: [this.data?.defaultPosChannel || null],
      due_date: [due_date || ''],
      autopay: [autopay || false],
      autopay_after_days: [autopay_after_days || 0, []],
      autoPushUpdatesToSalesOrder: [autoPushUpdatesToSalesOrder],
    });

    this.form
      .get('defaultPosChannel')
      .valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((channel :IChannel) => {
        this.form.patchValue(
          {
            sales_channel_id: channel?.id,
            warehouse_group_id: channel?.warehouseGroupId,
          },
          { emitEvent: true },
        );
      });

    this.form
      .get('autopay')
      ?.valueChanges.pipe(takeUntil(this.onDestroy$))
      .subscribe((autopay :boolean) => {
        this.updateAutopayAfterDaysValidator(autopay);
      });

    this.updateAutopayAfterDaysValidator(this.form.get('autopay')?.value);

    // Disabled it for view purpose only. Updates should be on Sales -> Sales Admin -> Invoice Types
    this.form.get('autoPushUpdatesToSalesOrder')?.disable();
  }

  private updateAutopayAfterDaysValidator(autopay :boolean) :void {
    const autopayAfterDaysControl = this.form.get('autopay_after_days');
    if (autopay) {
      autopayAfterDaysControl?.setValidators([
        Validators.required,
        Validators.pattern(/^(0|[1-9]\d*)$/),
      ]);
    } else {
      autopayAfterDaysControl?.clearValidators();
    }
    autopayAfterDaysControl?.updateValueAndValidity();
  }

  private initFromService() :void {
    this.currentCompany$ = this.clientCompanyApiService.currentCompany$;
  }

  private getCurrentCompany() :Observable<ICompany | null> {
    return this.currentCompany$.pipe(
      tap((company) => {
        this.currentCompany = company;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  private initObservables() :void {
    this.paymentTerms$ = this.settingsService.paymentTerms$;
  }

  get alternateIdsFormArray() :UntypedFormArray {
    return this.form.get('alternate_ids') as UntypedFormArray;
  }

  get salesChannelIdControl() :UntypedFormControl {
    return this.form.get('sales_channel_id') as UntypedFormControl;
  }

  get warehouseGroupIdControl() :UntypedFormControl {
    return this.form.get('warehouse_group_id') as UntypedFormControl;
  }

  get tagIds() :number[] {
    return this.form.get('tags')?.value;
  }

  get customFieldValues() :string {
    return this.form.get('customFieldValues')?.value;
  }

  get customFieldsEnabled() :boolean {
    return this.data?.customFieldsEnabled;
  }
}
